import { Component } from "@theme/component";
import { CartUpdateEvent } from "@theme/events";
import { fetchConfig } from "@theme/utilities";

/**
 * Returns a Shopify CDN image URL resized to the requested width via the
 * `?width=` query parameter. The Shopify CDN accepts this regardless of any
 * existing `_NxN` suffix in the path, so it also fixes small thumbnail URLs
 * returned by /recommendations/products.json. Falls back to the original URL
 * if parsing fails.
 *
 * @param {string | undefined | null} src
 * @param {number} width
 * @returns {string}
 */
function shopifyImageAtWidth(src, width) {
  if (!src) return "";
  try {
    const normalized = src.startsWith("//") ? `https:${src}` : src;
    const url = new URL(normalized, window.location.origin);
    url.searchParams.set("width", String(width));
    return url.toString();
  } catch {
    return src;
  }
}

/**
 * Returns the probe (sample) variant of a product, if one exists — i.e. a
 * variant whose title contains "2ml" (case-insensitive). Cart upsell is
 * probes-only, so this gate is also used to drop products that don't sell one.
 *
 * @param {{ variants?: Array<{ title?: string, available?: boolean }> }} product
 * @returns {object | null}
 */
function findProbeVariant(product) {
  if (!product || !Array.isArray(product.variants)) return null;
  return (
    product.variants.find((v) => {
      const title = (v && v.title ? String(v.title) : "").toLowerCase();
      return title.includes("2ml") && v.available !== false;
    }) ||
    product.variants.find((v) => {
      const title = (v && v.title ? String(v.title) : "").toLowerCase();
      return title.includes("2ml");
    }) ||
    null
  );
}

/**
 * Cart Upsell Component
 * Displays product recommendations based on cart contents
 */
class CartUpsellComponent extends Component {
  requiredRefs = ["productsContainer", "productTemplate"];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Map<string, any>} */
  #cachedRecommendations = new Map();

  /** @type {boolean} */
  #isLoading = false;

  connectedCallback() {
    super.connectedCallback();

    console.log("[Cart Upsell] Component connected", {
      sectionId: this.dataset.sectionId,
      maxProducts: this.dataset.maxProducts,
      intent: this.dataset.intent,
    });

    // Listen for cart updates to refresh recommendations
    document.addEventListener("cart:update", this.#handleCartUpdate.bind(this));

    // Load initial recommendations with a small delay to ensure cart is loaded
    setTimeout(() => {
      this.#loadRecommendations();
    }, 500);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelActiveFetch();
  }

  /**
   * Handle cart update events
   */
  #handleCartUpdate(event) {
    // Reload recommendations when cart changes
    console.log("Handle cart update events");
    this.#loadRecommendations();
  }

  /**
   * Cancel any active fetch request
   */
  #cancelActiveFetch() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
      this.#activeFetch = null;
    }
  }

  /**
   * Load product recommendations based on cart contents
   */
  async #loadRecommendations() {
    if (this.#isLoading) return;

    this.#isLoading = true;
    this.#cancelActiveFetch();

    try {
      console.log("[Cart Upsell] Loading recommendations...");

      // Fetch current cart
      const cart = await this.#fetchCart();
      console.log("[Cart Upsell] Cart data:", cart);

      if (!cart || cart.item_count === 0) {
        console.log("[Cart Upsell] Cart is empty, hiding component");
        this.setAttribute("hidden", "");
        this.#isLoading = false;
        return;
      }

      // Get product IDs from cart (exclude certain products if needed)
      const productIds = [
        ...new Set(cart.items.map((item) => item.product_id)),
      ];
      console.log("[Cart Upsell] Product IDs in cart:", productIds);

      if (productIds.length === 0) {
        this.setAttribute("hidden", "");
        this.#isLoading = false;
        return;
      }

      // Find best recommendations
      const recommendations = await this.#findBestRecommendations(
        productIds,
        cart
      );
      console.log("[Cart Upsell] Recommendations found:", recommendations);

      if (recommendations && recommendations.length > 0) {
        this.#renderRecommendations(recommendations, cart);
        this.removeAttribute("hidden");
        console.log(
          "[Cart Upsell] Rendered",
          recommendations.length,
          "recommendations"
        );

        // Smooth fade in
        this.style.opacity = "0";
        this.style.transition = "opacity 0.3s ease";
        requestAnimationFrame(() => {
          this.style.opacity = "1";
        });
      } else {
        console.log("[Cart Upsell] No recommendations, hiding component");
        this.setAttribute("hidden", "");
      }
    } catch (error) {
      console.error("[Cart Upsell] Error loading recommendations:", error);
      this.setAttribute("hidden", "");
    } finally {
      this.#isLoading = false;
    }
  }

  /**
   * Fetch current cart data
   */
  async #fetchCart() {
    this.#activeFetch = new AbortController();

    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      signal: this.#activeFetch.signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Find best product recommendations from cart items
   */
  async #findBestRecommendations(productIds, cart) {
    const maxProducts = parseInt(this.dataset.maxProducts) || 2;
    const intent = this.dataset.intent || "complementary";

    // Collect all recommendations from cart products
    const allRecommendations = [];
    const seenProductIds = new Set(productIds);

    for (const productId of productIds) {
      const recommendations = await this.#fetchProductRecommendations(
        productId,
        intent
      );

      if (recommendations && recommendations.length > 0) {
        // Filter out products already in cart, and only keep products that
        // have a probe (2ml) variant available — upsell is probes-only.
        const filtered = recommendations.filter(
          (rec) => !seenProductIds.has(rec.id) && findProbeVariant(rec)
        );

        allRecommendations.push(...filtered);
      }
    }

    // If no recommendations found, try fetching from collections as fallback
    if (allRecommendations.length === 0) {
      console.log(
        "[Cart Upsell] No Shopify recommendations, trying fallback..."
      );
      const fallbackProducts = await this.#fetchFallbackProducts(productIds);
      if (fallbackProducts && fallbackProducts.length > 0) {
        allRecommendations.push(
          ...fallbackProducts.filter((product) => findProbeVariant(product))
        );
      }
    }

    if (allRecommendations.length === 0) {
      console.log(
        "[Cart Upsell] No recommendations or fallback products found"
      );
      return [];
    }

    // Count frequency of each recommended product
    const frequencyMap = new Map();
    allRecommendations.forEach((product) => {
      const count = frequencyMap.get(product.id) || 0;
      frequencyMap.set(product.id, count + 1);

      // Store the product data
      if (!frequencyMap.has(`product_${product.id}`)) {
        frequencyMap.set(`product_${product.id}`, product);
      }
    });

    // Sort by frequency (most recommended first)
    const sortedProducts = Array.from(frequencyMap.entries())
      .filter(([key]) => key.toString().startsWith("product_"))
      .map(([key, product]) => ({
        product,
        frequency: frequencyMap.get(product.id),
      }))
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, maxProducts)
      .map(({ product }) => product);

    // Select best variant for each product based on cart contents
    return sortedProducts.map((product) =>
      this.#selectBestVariant(product, cart)
    );
  }

  /**
   * Fetch fallback products from all collections when Shopify recommendations fail
   */
  async #fetchFallbackProducts(excludeProductIds) {
    try {
      // Fetch products from the "all" collection
      const url = `${window.Shopify.routes.root}collections/all/products.json?limit=20`;
      const response = await fetch(url);

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      const products = data.products || [];

      // Filter out cart products and return random selection
      const filtered = products.filter(
        (product) => !excludeProductIds.includes(product.id)
      );

      // Shuffle and return
      return filtered.sort(() => Math.random() - 0.5);
    } catch (error) {
      console.error("[Cart Upsell] Error fetching fallback products:", error);
      return [];
    }
  }

  /**
   * Fetch product recommendations from Shopify API
   */
  async #fetchProductRecommendations(productId, intent) {
    // Check cache first
    const cacheKey = `${productId}_${intent}`;
    if (this.#cachedRecommendations.has(cacheKey)) {
      return this.#cachedRecommendations.get(cacheKey);
    }

    this.#activeFetch = new AbortController();

    try {
      const url = `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=10&intent=${intent}`;
      const response = await fetch(url, {
        signal: this.#activeFetch.signal,
      });

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      const recommendations = data.products || [];

      // Cache the results
      this.#cachedRecommendations.set(cacheKey, recommendations);

      return recommendations;
    } catch (error) {
      if (error.name === "AbortError") {
        return [];
      }
      console.error("Error fetching recommendations:", error);
      return [];
    }
  }

  /**
   * Select the best variant for a product based on cart contents.
   * For this store, cart upsell only surfaces probe (2ml) variants, so if one
   * exists we always prefer it over any cart-matching logic.
   */
  #selectBestVariant(product, cart) {
    if (!product.variants || product.variants.length === 0) {
      return product;
    }

    const probeVariant = findProbeVariant(product);
    if (probeVariant) {
      return {
        ...product,
        selectedVariant: probeVariant,
      };
    }

    // If only one variant, use it
    if (product.variants.length === 1) {
      return {
        ...product,
        selectedVariant: product.variants[0],
      };
    }

    // Check if product has options data
    if (!product.options || product.options.length === 0) {
      // No options data, just use first available variant
      const selectedVariant =
        product.variants.find((v) => v.available) || product.variants[0];
      return {
        ...product,
        selectedVariant,
      };
    }

    // Count option preferences from cart items
    const optionCounts = [];
    product.options.forEach(() => optionCounts.push(new Map()));

    cart.items.forEach((item) => {
      if (item.variant_options) {
        item.variant_options.forEach((optionValue, index) => {
          if (optionCounts[index]) {
            const currentCount = optionCounts[index].get(optionValue) || 0;
            optionCounts[index].set(optionValue, currentCount + item.quantity);
          }
        });
      }
    });

    // Find most popular option values
    const preferredOptions = product.options.map((option, index) => {
      const counts = optionCounts[index];
      let bestValue = null;
      let maxCount = -1;

      // Handle both array and object option formats
      const optionValues = Array.isArray(option.values)
        ? option.values
        : option.values
        ? Object.values(option.values)
        : [];

      for (const [value, count] of counts.entries()) {
        if (count > maxCount && optionValues.includes(value)) {
          maxCount = count;
          bestValue = value;
        }
      }

      return bestValue || optionValues[0] || option.values;
    });

    // Find matching variant
    let selectedVariant = product.variants.find((variant) => {
      // Handle variants that might not have options array
      const variantOptions =
        variant.options ||
        (variant.option1
          ? [variant.option1, variant.option2, variant.option3].filter(Boolean)
          : []);

      if (variantOptions.length !== preferredOptions.length) return false;
      return variantOptions.every((opt, i) => opt === preferredOptions[i]);
    });

    // Fallback to first available variant
    if (!selectedVariant) {
      selectedVariant =
        product.variants.find((v) => v.available) || product.variants[0];
    }

    return {
      ...product,
      selectedVariant,
    };
  }

  /**
   * Render recommendations to the DOM
   */
  #renderRecommendations(recommendations, cart) {
    const container = this.refs.productsContainer;
    const template = this.refs.productTemplate;

    if (!container || !template) return;

    // Clear existing products
    container.innerHTML = "";

    recommendations.forEach((product) => {
      const clone = template.content.cloneNode(true);
      const variant = product.selectedVariant;

      if (!variant) return;

      // Set product link
      const productUrl = `${product.url}?variant=${variant.id}`;
      clone.querySelectorAll('[ref="productLink"]').forEach((el) => {
        el.href = productUrl;
      });

      // Set product image
      const imageEl = clone.querySelector('[ref="productImage"]');
      if (imageEl) {
        // Handle multiple image format variations
        let imageSrc = "";

        // Try variant featured image (object or string)
        if (variant.featured_image) {
          imageSrc =
            typeof variant.featured_image === "string"
              ? variant.featured_image
              : variant.featured_image.src;
        }

        // Try product featured image (object or string)
        if (!imageSrc && product.featured_image) {
          imageSrc =
            typeof product.featured_image === "string"
              ? product.featured_image
              : product.featured_image.src;
        }

        // Try product image property
        if (!imageSrc && product.image) {
          imageSrc =
            typeof product.image === "string"
              ? product.image
              : product.image.src;
        }

        // Try first image in images array
        if (!imageSrc && product.images && product.images.length > 0) {
          const firstImage = product.images[0];
          imageSrc =
            typeof firstImage === "string" ? firstImage : firstImage.src;
        }

        console.log("[Cart Upsell] Image resolution:", {
          product: product.title,
          variantFeaturedImage: variant.featured_image,
          productFeaturedImage: product.featured_image,
          productImage: product.image,
          productImages: product.images,
          finalSrc: imageSrc,
        });

        if (imageSrc) {
          // Thumbnail slot is 60px desktop / 50px mobile. Serve a generous
          // srcset so high-DPR mobile screens get a crisp image.
          imageEl.src = shopifyImageAtWidth(imageSrc, 200);
          imageEl.srcset = [
            `${shopifyImageAtWidth(imageSrc, 200)} 1x`,
            `${shopifyImageAtWidth(imageSrc, 400)} 2x`,
            `${shopifyImageAtWidth(imageSrc, 600)} 3x`,
          ].join(", ");
        } else {
          imageEl.src = "";
          imageEl.removeAttribute("srcset");
        }
        imageEl.alt = product.title;
      }

      // Set product title
      const titleEl = clone.querySelector('[ref="productTitle"]');
      if (titleEl) {
        titleEl.href = productUrl;
        titleEl.textContent = product.title;

        // Add variant options if meaningful
        if (product.options && product.options.length > 0) {
          if (
            product.options.length > 1 ||
            product.options[0]?.name !== "Title"
          ) {
            const variantOptions =
              variant.options ||
              (variant.option1
                ? [variant.option1, variant.option2, variant.option3].filter(
                    Boolean
                  )
                : []);
            const variantInfo = variantOptions
              .filter((opt) => opt !== "Default Title")
              .join(", ");
            if (variantInfo) {
              titleEl.textContent = `${product.title} - ${variantInfo}`;
            }
          }
        }
      }

      // Set price
      const priceEl = clone.querySelector('[ref="productPrice"]');
      if (priceEl && variant.price) {
        priceEl.textContent = this.#formatMoney(variant.price);
      }

      // Set up form
      const formEl = clone.querySelector('[ref="productForm"]');
      const variantIdInput = clone.querySelector('[ref="variantIdInput"]');
      const addButton = clone.querySelector('[ref="addButton"]');

      if (formEl && variantIdInput && addButton) {
        variantIdInput.value = variant.id;

        // Add sections for section rendering
        const sectionsInput = formEl.querySelector('input[name="sections"]');
        if (sectionsInput) {
          sectionsInput.value = this.dataset.sectionId;
        }

        // Handle form submission
        formEl.addEventListener("submit", this.#handleAddToCart.bind(this));

        // Disable if not available
        if (!variant.available) {
          addButton.disabled = true;
          addButton.textContent = "Sold Out";
        }
      }

      container.appendChild(clone);
    });
  }

  /**
   * Handle add to cart form submission
   */
  async #handleAddToCart(event) {
    event.preventDefault();

    const form = event.target;
    const button = form.querySelector('button[type="submit"]');
    const formData = new FormData(form);

    if (button.disabled) return;

    // Disable button during request and swap its label for a spinner so we
    // don't flash English copy at the customer.
    const originalHTML = button.innerHTML;
    button.disabled = true;
    button.classList.add("is-loading");
    button.innerHTML =
      '<span class="cart-upsell-item__spinner" aria-hidden="true"></span>' +
      '<span class="visually-hidden">…</span>';

    try {
      const config = fetchConfig("json");
      config.body = JSON.stringify({
        id: formData.get("id"),
        quantity: formData.get("quantity"),
        sections: this.dataset.sectionId,
        sections_url: window.location.pathname,
      });

      const response = await fetch(
        `${window.Shopify.routes.root}cart/add.js`,
        config
      );

      if (!response.ok) {
        throw new Error(`Failed to add to cart: ${response.status}`);
      }

      const data = await response.json();

      // Dispatch cart update event
      document.dispatchEvent(
        new CartUpdateEvent({}, this.dataset.sectionId, {
          itemCount: data.item_count,
          source: "cart-upsell",
          sections: data.sections,
        })
      );

      // Reload recommendations
      setTimeout(() => {
        this.#loadRecommendations();
      }, 0);
    } catch (error) {
      console.error("Error adding product to cart:", error);
      button.classList.remove("is-loading");
      button.classList.add("is-error");
      button.innerHTML = "!";
      setTimeout(() => {
        button.classList.remove("is-error");
        button.innerHTML = originalHTML;
        button.disabled = false;
      }, 2000);
    }
  }

  /**
   * Format money using Shopify's money format
   */
  #formatMoney(cents) {
    // Try to get the format from Shopify's money_format settings
    const format = window.Shopify?.currency?.format || "€{{amount}}";
    const amount = (cents / 1).toFixed(2).replace(".", ",");
    return format
      .replace("{{amount}}", amount)
      .replace("{{amount_with_comma_separator}}", amount);
  }
}

customElements.define("cart-upsell-component", CartUpsellComponent);

export { CartUpsellComponent };
